/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { Close } from "@material-ui/icons";
import { ethers } from "ethers";
import { onValue, ref } from "firebase/database";
import { NETWORKS } from "../../constants";
import {
  getUserOptOutGauges,
  optInGauges,
  optOutGauges,
} from "../../scripts/scripts";

const readProviderFallback = new ethers.providers.JsonRpcProvider(
  NETWORKS.mainnet.rpcUrl
);

const MARKETS = [
  {
    id: "curve",
    label: "Curve",
    gaugesPath: "v2/gauges/gauges",
  },
  {
    id: "fxn",
    label: "f(x)",
    gaugesPath: "v2fxn/gauges/gauges",
  },
];

const FILTERS = [
  { id: "curve", label: "Curve" },
  { id: "fxn", label: "f(x)" },
  { id: "both", label: "Both" },
];

const EMPTY_STATE = {
  current: [],
  next: [],
  epoch: null,
  paused: false,
  canOptOut: false,
  minOptOutBalance: null,
};

function shortAddress(address) {
  if (!address || address.length < 10) return address || "";
  return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
}

function checksum(address) {
  try {
    return ethers.utils.getAddress(address);
  } catch {
    return address;
  }
}

function marketLabel(market) {
  return MARKETS.find((entry) => entry.id === market)?.label || market;
}

function activeMarketIds(filter) {
  return filter === "both" ? MARKETS.map((entry) => entry.id) : [filter];
}

function higherMin(a, b) {
  const toNumber = (value) => Number(String(value).replace(/,/g, ""));
  const left = toNumber(a);
  const right = toNumber(b);
  if (!Number.isFinite(left)) return b || a;
  if (!Number.isFinite(right)) return a;
  return left >= right ? a : b;
}

const OptOutSection = ({ address, provider, db, classes, connectedNetwork }) => {
  const [open, setOpen] = useState(false);
  const [marketFilter, setMarketFilter] = useState("curve");
  const [namesByMarket, setNamesByMarket] = useState({ curve: {}, fxn: {} });
  const [stateByMarket, setStateByMarket] = useState({
    curve: EMPTY_STATE,
    fxn: EMPTY_STATE,
  });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [optInSelected, setOptInSelected] = useState([]);
  const [pending, setPending] = useState(false);
  const [status, setStatus] = useState("");
  const [statusError, setStatusError] = useState(false);

  const readProvider = provider || readProviderFallback;
  const onMainnet = connectedNetwork === 1;
  const activeIds = activeMarketIds(marketFilter);
  const showMarket = marketFilter === "both";

  useEffect(() => {
    if (!db) return undefined;
    const unsubs = MARKETS.map((market) => {
      const gaugesRef = ref(db, market.gaugesPath);
      return onValue(gaugesRef, (snapshot) => {
        const data = snapshot.val();
        if (!data) return;
        const next = {};
        Object.entries(data).forEach(([gauge, pool]) => {
          if (!ethers.utils.isAddress(gauge)) return;
          const key = gauge.toLowerCase();
          next[key] =
            pool?.shortName || pool?.name || shortAddress(checksum(gauge));
        });
        setNamesByMarket((prev) => ({ ...prev, [market.id]: next }));
      });
    });
    return () => unsubs.forEach((unsub) => unsub());
  }, [db]);

  const loadOptOuts = async () => {
    if (!address || !readProvider) return;
    setLoading(true);
    const results = await Promise.all(
      MARKETS.map(async (market) => {
        const result = await getUserOptOutGauges(
          address,
          readProvider,
          market.id
        );
        return [
          market.id,
          {
            current: result.current || [],
            next: result.next || [],
            epoch: result.epoch,
            paused: Boolean(result.paused),
            canOptOut: Boolean(result.canOptOut),
            minOptOutBalance: result.minOptOutBalance || null,
          },
        ];
      })
    );
    setStateByMarket(Object.fromEntries(results));
    setLoading(false);
  };

  useEffect(() => {
    loadOptOuts();
  }, [address, provider]);

  const labelFor = (market, gauge) => {
    const name =
      namesByMarket[market]?.[gauge.toLowerCase()] || shortAddress(gauge);
    return showMarket ? `${marketLabel(market)} · ${name}` : name;
  };

  const nextEntries = useMemo(() => {
    const entries = [];
    activeIds.forEach((market) => {
      (stateByMarket[market]?.next || []).forEach((gauge) => {
        entries.push({ market, gauge });
      });
    });
    return entries;
  }, [stateByMarket, marketFilter]);

  const currentOnlyEntries = useMemo(() => {
    const entries = [];
    activeIds.forEach((market) => {
      const nextSet = new Set(
        (stateByMarket[market]?.next || []).map((gauge) => gauge.toLowerCase())
      );
      (stateByMarket[market]?.current || []).forEach((gauge) => {
        if (!nextSet.has(gauge.toLowerCase())) {
          entries.push({ market, gauge });
        }
      });
    });
    return entries;
  }, [stateByMarket, marketFilter]);

  const gaugeOptions = useMemo(() => {
    const options = [];
    activeIds.forEach((market) => {
      const nextSet = new Set(
        (stateByMarket[market]?.next || []).map((gauge) => gauge.toLowerCase())
      );
      Object.keys(namesByMarket[market] || {}).forEach((key) => {
        if (nextSet.has(key)) return;
        const value = checksum(key);
        options.push({
          market,
          value,
          label: labelFor(market, value),
        });
      });
    });
    options.sort((a, b) => a.label.localeCompare(b.label));
    return options;
  }, [namesByMarket, stateByMarket, marketFilter]);

  const pausedMarkets = activeIds.filter((market) => stateByMarket[market]?.paused);
  const canOptOut = activeIds.every((market) => stateByMarket[market]?.canOptOut);
  const minOptOutBalance = activeIds.reduce(
    (min, market) => higherMin(min, stateByMarket[market]?.minOptOutBalance),
    null
  );
  const epoch = stateByMarket.curve?.epoch ?? stateByMarket.fxn?.epoch;

  const changeFilter = (nextFilter) => {
    const allowed = new Set(activeMarketIds(nextFilter));
    setMarketFilter(nextFilter);
    setSelected((prev) => prev.filter((option) => allowed.has(option.market)));
    setOptInSelected((prev) =>
      prev.filter((entry) => allowed.has(entry.market))
    );
    setStatus("");
    setStatusError(false);
  };

  const toggleOptInGauge = (market, gauge) => {
    const key = gauge.toLowerCase();
    setOptInSelected((prev) =>
      prev.some(
        (entry) => entry.market === market && entry.address.toLowerCase() === key
      )
        ? prev.filter(
            (entry) =>
              !(entry.market === market && entry.address.toLowerCase() === key)
          )
        : [...prev, { market, address: checksum(gauge) }]
    );
  };

  const handleOpen = () => {
    setStatus("");
    setStatusError(false);
    setSelected([]);
    setOptInSelected([]);
    setOpen(true);
    loadOptOuts();
  };

  const handleClose = () => {
    if (pending) return;
    setOpen(false);
  };

  const handleWrite = async (action, entries) => {
    if (!provider || !onMainnet || !entries.length || pausedMarkets.length) return;
    if (action === "out" && !canOptOut) {
      setStatusError(true);
      setStatus(
        minOptOutBalance
          ? `Need at least ${minOptOutBalance} vlCVX locked for next epoch to opt out.`
          : "Need more vlCVX locked for next epoch to opt out."
      );
      return;
    }

    const grouped = {};
    entries.forEach((entry) => {
      const market = entry.market;
      const gauge = entry.value || entry.address;
      if (!grouped[market]) grouped[market] = [];
      grouped[market].push(gauge);
    });

    setPending(true);
    setStatusError(false);
    const verb = action === "out" ? "opt-out" : "opt-in";
    let failed = false;
    for (const market of Object.keys(grouped)) {
      setStatus(`Submitting ${marketLabel(market)} ${verb}…`);
      const result =
        action === "out"
          ? await optOutGauges(provider, grouped[market], market)
          : await optInGauges(provider, grouped[market], market);
      if (!result.success) {
        failed = true;
        setStatusError(true);
        setStatus(
          result.error || `${marketLabel(market)} transaction failed.`
        );
        break;
      }
    }
    if (!failed) {
      setStatus(
        action === "out"
          ? "Opted out. Changes take effect next epoch."
          : "Opted in. Changes take effect next epoch."
      );
      setSelected([]);
      setOptInSelected([]);
    }
    await loadOptOuts();
    setPending(false);
  };

  const writesDisabled =
    !onMainnet || pending || !provider || pausedMarkets.length > 0;

  const nextCount =
    (stateByMarket.curve?.next.length || 0) +
    (stateByMarket.fxn?.next.length || 0);
  const currentCount =
    (stateByMarket.curve?.current.length || 0) +
    (stateByMarket.fxn?.current.length || 0);
  const summary = loading
    ? "..."
    : nextCount === 0 && currentCount === 0
      ? "None"
      : nextCount === 0
        ? `${currentCount} this epoch`
        : `${nextCount} gauge${nextCount === 1 ? "" : "s"}`;

  return (
    <>
      <Typography className={classes.tableSubText} component="span">
        {summary}
        <Button
          classes={{
            root: classes.delegateButton,
            disabled: classes.delegateButtonDisabled,
          }}
          disableRipple
          onClick={handleOpen}
        >
          Manage
        </Button>
      </Typography>
      <Dialog
        open={open}
        onClose={handleClose}
        classes={{ paper: classes.optOutDialogPaper }}
        maxWidth="sm"
        fullWidth
        disableAutoFocus
        disableEnforceFocus
      >
        <DialogTitle disableTypography className={classes.optOutDialogTitle}>
          <Typography component="span" className={classes.optOutDialogTitleText}>
            Reward Opt-Out
          </Typography>
          <IconButton
            size="small"
            className={classes.optOutCloseButton}
            onClick={handleClose}
            aria-label="Close"
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.optOutDialogContent}>
          <Typography className={classes.optOutBodyText}>
            Opt out of receiving Votium rewards for your votes on selected
            gauges.
          </Typography>
          <div className={classes.optOutMarketToggle}>
            {FILTERS.map((filter) => (
              <Button
                key={filter.id}
                disableRipple
                className={`${classes.optOutMarketButton} ${
                  marketFilter === filter.id
                    ? classes.optOutMarketButtonSelected
                    : ""
                }`}
                onClick={() => changeFilter(filter.id)}
                disabled={pending}
              >
                {filter.label}
              </Button>
            ))}
          </div>
          {pausedMarkets.length > 0 && (
            <Typography className={classes.optOutHint}>
              Opt-outs are currently paused for{" "}
              {pausedMarkets.map(marketLabel).join(" and ")}.
            </Typography>
          )}
          {!onMainnet && (
            <Typography className={classes.optOutHint}>
              Connect to Ethereum to change your opt-out list.
            </Typography>
          )}
          {!canOptOut && onMainnet && !loading && (
            <Typography className={classes.optOutHint}>
              Opt-out requires at least {minOptOutBalance || "the minimum"} vlCVX
              locked for the next epoch.
            </Typography>
          )}
          <Autocomplete
            multiple
            id="opt-out-gauge-select"
            options={gaugeOptions}
            value={selected}
            onChange={(_, value) => setSelected(value || [])}
            getOptionLabel={(option) => option.label}
            getOptionSelected={(option, value) =>
              option.market === value.market &&
              option.value.toLowerCase() === value.value.toLowerCase()
            }
            filterSelectedOptions
            disabled={writesDisabled}
            classes={{
              listbox: classes.optOutListbox,
              option: classes.optOutOption,
              popupIndicator: classes.optOutPopupIndicator,
              clearIndicator: classes.optOutClearIndicator,
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={`${option.market}:${option.value}` || key}
                    {...tagProps}
                    size="small"
                    label={option.label}
                    className={classes.optOutChip}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                className={classes.optOutInput}
                label="Select gauges"
                placeholder="Search pools"
                variant="outlined"
              />
            )}
          />
          <div className={classes.optOutActions}>
            <Button
              className={classes.optOutPrimaryButton}
              disableRipple
              disabled={writesDisabled || !canOptOut || selected.length === 0}
              onClick={() => handleWrite("out", selected)}
            >
              {pending ? (
                <CircularProgress size={16} />
              ) : (
                `Opt out${selected.length ? ` (${selected.length})` : ""}`
              )}
            </Button>
          </div>
          <Typography className={classes.optOutSectionLabel}>
            Opted out gauges
            {epoch != null ? ` (next epoch ${epoch + 1})` : ""}
          </Typography>
          {loading ? (
            <CircularProgress size={16} />
          ) : nextEntries.length === 0 ? (
            <Typography className={classes.optOutHint}>None</Typography>
          ) : (
            <div className={classes.optOutList}>
              {nextEntries.map(({ market, gauge }) => {
                const checked = optInSelected.some(
                  (entry) =>
                    entry.market === market &&
                    entry.address.toLowerCase() === gauge.toLowerCase()
                );
                return (
                  <FormControlLabel
                    key={`${market}:${gauge}`}
                    className={classes.optOutListItem}
                    title={gauge}
                    control={
                      <Checkbox
                        size="small"
                        color="default"
                        className={classes.optOutCheckbox}
                        checked={checked}
                        disabled={writesDisabled}
                        onChange={() => toggleOptInGauge(market, gauge)}
                      />
                    }
                    label={labelFor(market, gauge)}
                  />
                );
              })}
            </div>
          )}
          {nextEntries.length > 0 && (
            <div className={classes.optOutActions}>
              <Button
                className={classes.optOutSecondaryButton}
                disableRipple
                disabled={writesDisabled || optInSelected.length === 0}
                onClick={() => handleWrite("in", optInSelected)}
              >
                {pending ? (
                  <CircularProgress size={16} />
                ) : (
                  `Opt in${
                    optInSelected.length ? ` (${optInSelected.length})` : ""
                  }`
                )}
              </Button>
            </div>
          )}
          {currentOnlyEntries.length > 0 && (
            <Typography className={classes.optOutHint}>
              Still opted out this epoch
              {epoch != null ? ` (${epoch})` : ""}:{" "}
              {currentOnlyEntries
                .map(({ market, gauge }) => labelFor(market, gauge))
                .join(", ")}
            </Typography>
          )}
          {status && (
            <Typography
              className={classes.optOutHint}
              style={
                statusError
                  ? { color: "#e57373", textAlign: "center" }
                  : { textAlign: "center" }
              }
            >
              {status}
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OptOutSection;
